import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import CLUB from '../../config/club.js'
import { PBCC_PREMIUM, HUSTLER } from '../../config/siteMode.js'
import { watchMyGames, onMemberChange, getMemberId, watchMyResults, loadResult, deleteResult, deleteMemberGame } from '../../lib/memberGgc.js'
import { Trash2 } from 'lucide-react'

// Saved Games list PAGE — reached by clicking the "Saved Games" tile on Golf Game
// Central. Lists the signed-in member's own games (read /memberGames/{uid} → each
// opens its /ggGames/{id} by id; never a collection query). Tapping an entry
// reopens it in the setup form, fully editable, saving back to the SAME record.
const PRIMARY = CLUB.primaryColor || 'var(--ge-primary)'
const ACCENT = CLUB.accentColor || 'var(--ge-primary)'
const TITLE_ON_DARK = HUSTLER ? 'var(--ge-primary)' : (PBCC_PREMIUM ? 'var(--ge-gold)' : PRIMARY)
const SUB_ON_DARK = HUSTLER ? '#4b5563' : (PBCC_PREMIUM ? '#D8CFB8' : '#6B7280')

function SavedGameCard({ g }) {
  const when = g.updatedAt ? new Date(g.updatedAt).toLocaleDateString() : ''
  const status = g.status === 'final' ? 'Final' : 'Active'
  const [busy, setBusy] = useState(false)
  // Delete this saved game: confirm, then remove /ggGames/{id} + the member index
  // (also clears it from the app). The list is a live listener, so the card
  // disappears on success. preventDefault/stopPropagation so it doesn't open the
  // card's Link.
  const onDelete = async (e) => {
    e.preventDefault(); e.stopPropagation()
    if (busy) return
    if (!window.confirm(`Delete "${g.name || 'this game'}"? This removes it from your account and the app.`)) return
    setBusy(true)
    try { await deleteMemberGame(g.id) }
    catch (err) { alert((err && err.message) || 'Could not delete the game.'); setBusy(false) }
  }
  return (
    <div className="relative">
      <Link to={`/admin/gg/games?edit=${g.id}`} className="rounded-2xl p-4 block transition active:scale-[0.99] hover:shadow-lg"
        style={{ background: CLUB.creamColor, border: `1px solid ${ACCENT}44`, textDecoration: 'none' }}>
        <div className="flex items-center justify-between gap-3 pr-8">
          <div className="min-w-0">
            <div className="text-base font-extrabold truncate" style={{ color: PRIMARY }}>{g.name || 'Wolf'}</div>
            <div className="text-sm text-gray-600 mt-1">{(g.gameId || 'wolf')} · {status}{when ? ` · ${when}` : ''}</div>
          </div>
          <span className="text-sm font-bold whitespace-nowrap" style={{ color: PRIMARY }}>Open ›</span>
        </div>
      </Link>
      <button type="button" onClick={onDelete} disabled={busy} title="Delete game" aria-label="Delete game"
        className="absolute top-2 right-2 rounded-lg p-1.5"
        style={{ background: '#fff', border: '1px solid #E5C9C9', color: '#B42318', lineHeight: 0, opacity: busy ? 0.5 : 1 }}>
        {busy ? '…' : <Trash2 size={16} />}
      </button>
    </div>
  )
}

// ── My Saved Results — completed-game snapshots saved from the companion app
// (/memberResults index → /ggResults/{id}). Own-only; view + delete. Delete
// removes both nodes. Read-only view (never reopened to play). ──
const money = (v) => (v > 0 ? `+$${v}` : v < 0 ? `−$${Math.abs(v)}` : '$0')
const cellFmt = (v) => (v == null ? '—' : v > 0 ? `+${v}` : v < 0 ? `−${Math.abs(v)}` : '·')
const posNeg = (v) => (v > 0 ? '#1B7A3E' : v < 0 ? '#B42318' : '#5B6B60')
const fmtDate = (ms) => { if (!ms) return ''; try { return new Date(ms).toLocaleDateString() } catch { return '' } }

function ResultDetail({ rec }) {
  const players = Array.isArray(rec.players) ? rec.players : []
  const settle = Array.isArray(rec.settleUp) ? rec.settleUp : []
  const has = {
    main: players.some((p) => p.main != null),
    scats: players.some((p) => p.scats != null),
    skins: players.some((p) => p.skins != null),
    dots: players.some((p) => p.dots != null),
    cth: players.some((p) => p.cth != null),
  }
  const anySide = has.scats || has.skins || has.dots || has.cth
  const th = { color: '#5B6B60', fontSize: 11, fontWeight: 800, textTransform: 'uppercase', textAlign: 'right', padding: '5px 4px' }
  const td = { fontSize: 14, textAlign: 'right', padding: '5px 4px', fontWeight: 600, color: PRIMARY }
  return (
    <div style={{ marginTop: 12, borderTop: '1px solid #0000001a', paddingTop: 12 }}>
      {players.map((p, i) => (
        <div key={i} className="flex justify-between" style={{ padding: '2px 0' }}>
          <span style={{ fontSize: 15, fontWeight: 700, color: '#0F2914' }}>{p.name}</span>
          <span style={{ fontSize: 15, fontWeight: 800, color: posNeg(p.total) }}>{money(p.total)}</span>
        </div>
      ))}
      <div style={{ height: 1, background: '#0000001a', margin: '8px 0' }} />
      {settle.length === 0
        ? <div style={{ fontSize: 13, color: '#5B6B60', fontStyle: 'italic' }}>All square — nobody owes anybody.</div>
        : settle.map((t, i) => (
          <div key={i} className="flex justify-between" style={{ padding: '2px 0' }}>
            <span style={{ fontSize: 14, color: '#0F2914' }}><b>{t.from}</b> → <b>{t.to}</b></span>
            <span style={{ fontSize: 15, fontWeight: 800, color: PRIMARY }}>${t.amt}</span>
          </div>
        ))}
      {anySide && (
        <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: 12 }}>
          <thead><tr>
            <th style={{ ...th, textAlign: 'left' }}>Player</th>
            {has.main ? <th style={th}>{rec.mainLabel || 'Game'}</th> : null}
            {has.scats ? <th style={th}>Scats</th> : null}
            {has.skins ? <th style={th}>Skins</th> : null}
            {has.dots ? <th style={th}>Dots</th> : null}
            {has.cth ? <th style={th}>CTH</th> : null}
            <th style={th}>Total</th>
          </tr></thead>
          <tbody>
            {players.map((p, i) => (
              <tr key={i} style={{ borderTop: '1px solid #0000000f' }}>
                <td style={{ fontSize: 14, color: '#0F2914', fontWeight: 700, padding: '5px 4px' }}>{p.name}</td>
                {has.main ? <td style={td}>{cellFmt(p.main)}</td> : null}
                {has.scats ? <td style={td}>{cellFmt(p.scats)}</td> : null}
                {has.skins ? <td style={td}>{cellFmt(p.skins)}</td> : null}
                {has.dots ? <td style={td}>{cellFmt(p.dots)}</td> : null}
                {has.cth ? <td style={td}>{cellFmt(p.cth)}</td> : null}
                <td style={{ ...td, fontWeight: 800, color: posNeg(p.total) }}>{money(p.total)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {rec.summary ? <div style={{ fontSize: 12, color: '#5B6B60', marginTop: 10 }}>{rec.summary}</div> : null}
    </div>
  )
}

function SavedResults({ uid }) {
  const [rows, setRows] = useState([])
  const [open, setOpen] = useState(null)     // { id, rec }
  const [busyId, setBusyId] = useState(null)
  useEffect(() => { if (!uid) { setRows([]); return undefined } return watchMyResults(setRows) }, [uid])

  const toggle = async (id) => {
    if (open?.id === id) { setOpen(null); return }
    const rec = await loadResult(id)
    setOpen(rec ? { id, rec } : null)
  }
  const del = async (id, e) => {
    e.preventDefault(); e.stopPropagation()
    if (!window.confirm('Delete this saved result? This removes it from your account.')) return
    setBusyId(id)
    try { await deleteResult(id); if (open?.id === id) setOpen(null) }
    catch { window.alert('Could not delete — try again.') }
    finally { setBusyId(null) }
  }

  return (
    <div className="space-y-3">
      <div>
        <h2 className="text-xl font-extrabold" style={{ color: TITLE_ON_DARK }}>My Saved Results</h2>
        <p className="text-sm mt-1" style={{ color: SUB_ON_DARK }}>Completed games you saved from the app. Tap to view; delete to remove.</p>
      </div>
      {rows.length === 0 ? (
        <p className="text-sm" style={{ color: SUB_ON_DARK }}>No saved results yet. Finish a game in the app and tap “Save results to site”.</p>
      ) : rows.map((r) => (
        <div key={r.id} onClick={() => toggle(r.id)} className="rounded-2xl p-4 cursor-pointer transition hover:shadow-lg"
          style={{ background: CLUB.creamColor, border: `1px solid ${ACCENT}44` }}>
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <div className="text-base font-extrabold truncate" style={{ color: PRIMARY }}>{r.icon ? r.icon + ' ' : ''}{r.title || r.gameId}</div>
              <div className="text-sm text-gray-600 mt-1">{[r.course, r.winnerLine, fmtDate(r.savedAt)].filter(Boolean).join(' · ')}</div>
            </div>
            <button onClick={(e) => del(r.id, e)} disabled={busyId === r.id}
              className="text-xs font-extrabold rounded-lg whitespace-nowrap"
              style={{ flexShrink: 0, color: '#C62828', border: '1px solid #C62828', background: 'transparent', padding: '5px 10px', cursor: 'pointer', opacity: busyId === r.id ? 0.5 : 1 }}>
              {busyId === r.id ? '…' : 'Delete'}
            </button>
          </div>
          {open?.id === r.id && open.rec ? <ResultDetail rec={open.rec} /> : null}
        </div>
      ))}
    </div>
  )
}

export default function GSavedGames() {
  const [uid, setUid] = useState(getMemberId())
  const [games, setGames] = useState([])
  useEffect(() => onMemberChange(setUid), [])
  // Seasons live under the same /memberGames index (kind:'season') but have
  // their own Season tab — keep them out of the one-off Saved Games list.
  useEffect(() => { if (!uid) { setGames([]); return undefined } return watchMyGames((gs) => setGames(gs.filter((g) => g.kind !== 'season'))) }, [uid])

  return (
    <div className="space-y-8">
      <div className="space-y-5">
        <div>
          <h1 className="text-2xl font-extrabold" style={{ color: TITLE_ON_DARK }}>Saved Games</h1>
          <p className="text-sm mt-1" style={{ color: SUB_ON_DARK }}>Open a saved game to resume or edit it — your changes sync to the app.</p>
        </div>

        {!uid ? (
          <p className="text-sm" style={{ color: SUB_ON_DARK }}>Sign in to see your saved games.</p>
        ) : games.length === 0 ? (
          <p className="text-sm" style={{ color: SUB_ON_DARK }}>No saved games yet. Set up a game on Golf Game Central, then Review → Send and Save — it will appear here to reopen and edit.</p>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">{games.map((g) => <SavedGameCard key={g.id} g={g} />)}</div>
        )}
      </div>

      {uid ? <SavedResults uid={uid} /> : null}
    </div>
  )
}
